export function escapeString (string) {
  const escString = JSON.stringify(string)
  return escString.substring(1, escString.length - 1)
}

export function buildJobToCompare (job, jobOutput) {
  const { jobOutputPathKey, analysisId, objectiveId, outputFiles } = jobOutput
  const { sourceFilePathKey, jobPathKey, filename, isNested, nestedJobName, nestedJobs, config } = job

  return {
    jobOutputPathKey,
    analysisId,
    objectiveId,
    outputFiles,
    sourceFilePathKey,
    jobPathKey,
    isNested,
    filename,
    nestedJobs,
    nestedJobName,
    config
  }
}

export function errorHandlingAppSync (response) {
  if (response.data && response.data.hasOwnProperty('errors') && response.data.errors.length > 0) {
    const error = new Error(response.data.errors[0].message)
    if (response.data.errors[0].code) {
      error.code = response.data.errors[0].code
    }
    throw error
  }
  return parseAppSyncData(response.data)
}

function parseAppSyncData (data, dataKey, result = {}) {
  if (typeof data !== 'object' || data === null) {
    return dataKey !== 'config' ? data : JSON.parse(data)
  }

  if (!Array.isArray(data)) {
    for (const [key, value] of Object.entries(data)) {
      if (key === 'item' || key === 'items') {
        result = parseAppSyncData(value, key)
      } else {
        result[key] = parseAppSyncData(value, key)
      }
    }
  } else {
    if (dataKey === 'item') {
      const entries = data[0] ? Object.entries(data[0]) : []
      for (const [key, value] of entries) {
        result[key] = parseAppSyncData(value, key)
      }
    } else {
      result = data.map(elem => parseAppSyncData(elem))
    }
  }
  return result
}

export function downloadFileBlob (data, filename, mime) {
  const blob = new Blob([data], {type: mime || 'application/octet-stream'})
  if (typeof window.navigator.msSaveBlob !== 'undefined') {
    // IE workaround for "HTML7007: One or more blob URLs were
    // revoked by closing the blob for which they were created.
    // These URLs will no longer resolve as the data backing
    // the URL has been freed."
    window.navigator.msSaveBlob(blob, filename)
  } else {
    const blobURL = window.URL.createObjectURL(blob)
    const tempLink = document.createElement('a')
    tempLink.style.display = 'none'
    tempLink.href = blobURL
    tempLink.setAttribute('download', filename)

    // Safari thinks _blank anchor are pop ups. We only want to set _blank
    // target if the browser does not support the HTML5 download attribute.
    // This allows you to download files in desktop safari if pop up blocking
    // is enabled.
    if (typeof tempLink.download === 'undefined') {
      tempLink.setAttribute('target', '_blank')
    }

    document.body.appendChild(tempLink)
    tempLink.click()
    document.body.removeChild(tempLink)
    window.URL.revokeObjectURL(blobURL)
  }
}

export function jobDescription (analysisId, objectiveId, compensated, forViewer, isShortName) {
  const description = []
  const descriptionCompensated = []
  const descriptionForViewer = []
  const descriptionShort = []

  // First - analysisId, second - objective
  descriptionShort[0] = []
  descriptionShort[0][0] = 'nested'
  descriptionShort[1] = []
  descriptionShort[1][0] = 'manual'
  descriptionShort[3] = []
  descriptionShort[3][0] = 'manual'
  descriptionShort[4] = []
  descriptionShort[4][10] = 'min. supp. vol.'
  descriptionShort[4][11] = 'min. supp. rm'
  descriptionShort[4][14] = 'min. time'
  descriptionShort[4][20] = 'expert'
  descriptionShort[4][21] = 'cust. weight.'
  descriptionShort[4][22] = 'max. dist.'
  descriptionShort[5] = []
  descriptionShort[5][1] = 'min. dist.'
  descriptionShort[5][14] = 'min. time'
  descriptionShort[99] = []
  descriptionShort[99][0] = 'manual'

  description[0] = []
  description[0][0] = 'Nested optimization'
  description[1] = []
  description[1][0] = 'Manual orientation without thermal distortion'
  description[3] = []
  description[3][0] = 'Manual orientation with thermal distortion'
  description[4] = []
  description[4][10] = 'Optimization for minimum support volume'
  description[4][11] = 'Optimization for minimum support removal'
  description[4][14] = 'Optimization for minimum build time'
  description[4][20] = 'Optimization based on expert knowledge'
  description[4][21] = 'Optimization based on custom weighting'
  description[4][22] = 'Build Time Optimization Based on Maximum Distortion'
  description[5] = []
  description[5][1] = 'Optimization for minimum thermal distortion'
  description[5][14] = 'Optimization for build time with maximum distortion'
  description[99] = []
  description[99][0] = 'Manual orientation with modified support'

  descriptionCompensated[0] = []
  descriptionCompensated[0][0] = 'Nested optimization'
  descriptionCompensated[1] = []
  descriptionCompensated[1][0] = 'Compensated manual orientation without thermal distortion'
  descriptionCompensated[3] = []
  descriptionCompensated[3][0] = 'Compensated manual orientation with thermal distortion'
  descriptionCompensated[4] = []
  descriptionCompensated[4][10] = 'Compensated optimization for minimum support volume'
  descriptionCompensated[4][11] = 'Compensated optimization for minimum support removal'
  descriptionCompensated[4][14] = 'Compensated optimization for minimum build time'
  descriptionCompensated[4][20] = 'Compensated optimization based on expert knowledge'
  descriptionCompensated[4][21] = 'Compensated optimization based on custom weighting'
  descriptionCompensated[4][22] = 'Build Time Compensated Optimization Based on Maximum Distortion'
  descriptionCompensated[5] = []
  descriptionCompensated[5][1] = 'Compensated optimization for minimum thermal distortion'
  descriptionCompensated[5][14] = 'Compensated optimization for build time with maximum distortion'
  descriptionCompensated[99] = []
  descriptionCompensated[99][0] = 'Compensated manual orientation with modified support'

  descriptionForViewer[0] = []
  descriptionForViewer[0][0] = 'Nested'
  descriptionForViewer[1] = []
  descriptionForViewer[1][0] = 'manual orientation without thermal distortion'
  descriptionForViewer[3] = []
  descriptionForViewer[3][0] = 'manual orientation with thermal distortion'
  descriptionForViewer[4] = []
  descriptionForViewer[4][10] = 'for minimum support volume'
  descriptionForViewer[4][11] = 'for minimum support removal'
  descriptionForViewer[4][14] = 'for minimum build time'
  descriptionForViewer[4][20] = 'based on expert knowledge'
  descriptionForViewer[4][21] = 'based on custom weighting'
  descriptionForViewer[4][22] = 'for Build Time Based on Maximum Distortion'
  descriptionForViewer[5] = []
  descriptionForViewer[5][1] = 'for minimum thermal distortion'
  descriptionForViewer[5][14] = 'for build time with maximum distortion'
  descriptionForViewer[99] = []
  descriptionForViewer[99][0] = 'for manual orientation with modified support'

  if (forViewer) {
    if (descriptionForViewer[analysisId] && descriptionForViewer[analysisId][objectiveId]) {
      if (compensated) {
        return descriptionForViewer[analysisId][objectiveId] + ', compensated'
      } else {
        return descriptionForViewer[analysisId][objectiveId]
      }
    } else {
      return `No jobDescription yet: analysisId ${analysisId}, objectiveId ${objectiveId}`
    }
  } else if (isShortName) {
    if (descriptionShort[analysisId] && descriptionShort[analysisId][objectiveId]) {
      return descriptionShort[analysisId][objectiveId]
    } else {
      return `No description`
    }
  } else {
    if (compensated) {
      if (descriptionCompensated[analysisId] && descriptionCompensated[analysisId][objectiveId]) {
        return descriptionCompensated[analysisId][objectiveId]
      } else {
        return `No jobDescription yet: analysisId ${analysisId}, objectiveId ${objectiveId}`
      }
    } else {
      if (description[analysisId] && description[analysisId][objectiveId]) {
        return description[analysisId][objectiveId]
      } else {
        return `No jobDescription yet: analysisId ${analysisId}, objectiveId ${objectiveId}`
      }
    }
  }
}

export const defaultConfig = {
  'N_part': [],
  'filename': '',
  'printer': 'EOSM290',
  'unit': 'mm',
  'scaling': [1],
  'material': [1],
  'RotationalAngle': [],
  'Weights': [0.5, 0.5, 0.5],
  'layerThickness': [40],
  'elementsize': [0.002],
  'Power_laser': [200],
  'NLaser': [1],
  'VlaserSupp': [1000],
  'hatch': [0.11],
  'Diameter_laser': [100],
  'Speed_laser': [400],
  'suppSpacing_xy': [1.0],
  'suppHatchSize': [3.5],
  'supp_size_boundary': [0.6],
  'hsub': [5],
  'critical_angle': [40],
  'Tplate': [80],
  'isCompensate': [0],
  'isSupport': [1],
  'isHeatTreatment': true,
  'isMinPeeling': false,
  'MaxDist': [-1],
  'AllowedFaces': [],
  'RestrictedFaces': [],
  'AngleRestriction': [],
  'scalingX_material': [0],
  'scalingY_material': [0],
  'Wt': [0.5],
  'Pt': [0.16],
  'Tt': [0.5],
  'Ot': [0.8],
  'Wb': [0.8],
  'Pb': [0.16],
  'Tb': [0.5],
  'Ob': [0.8],
  'fraction_avg': [0.42],
  'isRepair': false,
  'isEvalFacet': true,
  'isEvalVertex': true,
  'isEvalEdge': true,
  'isFragmentation': false,
  'H_buildPlate': [30],
  'CrashTolerance': [0.4],
  'fragmentationSize': [5.0],
  'tetraEps': [0.001],
  'tetraMeshSize': [2.0],
  'PrinterXYZ': [],
  'DirectionRecoater': [],
  'plateThickness': [30],
  'material_name': '',
  'nu': [],
  'E': [],
  'E_heat': [],
  'rho': [],
  'Cp': [],
  'k': [],
  'Tsolid': [],
  'Tliquid': [],
  'sigma_y': [],
  'sigma_y_heat': [],
  'hsl': [],
  'alpha': [],
  'filename_supp': [],
  'unit_supp': 'mm',
  'needRemesh': false,
  'isStrengthSupp': [0],
  'compensationScale': [1.0]
}



// WEBPACK FOOTER //
// ./src/services/helpers.js